import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import SMSVerification from '../components/SMSVerification/SMSVerification';
import CampaignCalculator from '../components/CampaignCalculator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../config/api';
import addressService from '../services/addressService';
import parameterService from '../services/parameterService';
import cityDistrictService from '../services/cityDistrictService';
import {
  faShippingFast,
  faUser,
  faPhone,
  faEnvelope,
  faCity,
  faBuilding,
  faHome,
  faShoppingCart,
  faCreditCard,
  faTag,
  faCheck,
  faTimes,
  faMapMarkerAlt,
  faShoppingBag,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import './Checkout.css';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

function Checkout() {
  const { cart, clearCart, discount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [buttonText, setButtonText] = useState('');
  const [error, setError] = useState(null);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [isValidatingStock, setIsValidatingStock] = useState(true);
  const [selectedCity, setSelectedCity] = useState('');
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    district: '',
    address: '',
    cardHolderName: '',
    cardNumber: '',
    expireMonth: '',
    expireYear: '',
    cvc: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('creditCard'); // 'creditCard' veya 'cashOnDelivery'

  // Payment method değiştiğinde SMS doğrulama state'ini reset et
  useEffect(() => {
    if (paymentMethod === 'cashOnDelivery') {
      setSmsVerificationRequired(true);
      setSmsVerified(false);
      setVerificationCode('');
    } else {
      setSmsVerificationRequired(false);
      setSmsVerified(false);
      setVerificationCode('');
    }
  }, [paymentMethod]);

  const [installmentOptions, setInstallmentOptions] = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState('1');

  const GELIVER_API_KEY = '4b57b2d3-110a-4ac8-bb02-284c83b91c5b';

  const [showThreeDSecure, setShowThreeDSecure] = useState(false);
  const [threeDSecureContent, setThreeDSecureContent] = useState('');

  const containerRef = useRef(null);

  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [distanceSalesAccepted, setDistanceSalesAccepted] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [binNumber, setBinNumber] = useState('');
  const [isValidBin, setIsValidBin] = useState(false);

  // SMS Doğrulama state'leri
  const [smsVerificationRequired, setSmsVerificationRequired] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  
  // Kampanya state'i
  const [campaignData, setCampaignData] = useState(null);

  // Sayfa yüklendiğinde stok kontrolü ve kargo ücreti hesaplama
  useEffect(() => {
    validateCartStock();
    loadCities();
    calculateShipping(); // Kargo ücretini hesapla
  }, [cart, paymentMethod]);

  // İlleri JSON dosyalarından çek
  const loadCities = async () => {
    setLoadingCities(true);
    try {
      const citiesData = await cityDistrictService.loadCities();
      
      if (citiesData && citiesData.length > 0) {
        setCities(citiesData);
        console.log('✅ İl listesi yüklendi:', citiesData.length, 'il');
      } else {
        toast.error('İl bilgileri yüklenirken hata oluştu');
      }
    } catch (error) {
      console.error('İl bilgileri yükleme hatası:', error);
      toast.error(error.message || 'İl bilgileri yüklenirken hata oluştu');
    } finally {
      setLoadingCities(false);
    }
  };

  // İlçeleri JSON dosyalarından çek
  const loadDistricts = async (cityId) => {
    setLoadingDistricts(true);
    try {
      const districtsData = await cityDistrictService.loadDistrictsForCity(cityId);
      
      if (districtsData && districtsData.length > 0) {
        // Teslimat alanında olan ilçeleri filtrele
        const deliveryDistricts = cityDistrictService.filterDistrictsByDelivery(districtsData, true);
        setDistricts(deliveryDistricts);
        console.log('✅ İlçe listesi yüklendi:', deliveryDistricts.length, 'ilçe');
      } else {
        toast.error('İlçe bilgileri yüklenirken hata oluştu');
        setDistricts([]);
      }
    } catch (error) {
      console.error('İlçe bilgileri yükleme hatası:', error);
      toast.error(error.message || 'İlçe bilgileri yüklenirken hata oluştu');
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Stok kontrolü fonksiyonu
  const validateCartStock = async () => {
    if (!cart || cart.length === 0) {
      setIsValidatingStock(false);
      return;
    }
    
    setIsValidatingStock(true);
    const outOfStock = [];
    
    try {
      // Her sepet öğesi için stok kontrolü yap
      for (const item of cart) {
        // Custom design ve normal ürünler için farklı kontroller
        const isCustom = item.is_custom;
        const productId = item.product_id || item.product.id;
        const modelId = item.selectedModel?.id || item.phone_model?.id || item.phone_model_id;
        const colorId = item.selectedColor?.id || item.color?.id || item.color_id || 0;
        
        try {
          // API endpoint'i belirle
          const endpoint = isCustom 
            ? `/phone-models/${modelId}/colors/${colorId}/custom-design-stock?product_type_id=1`
            : `/products/${productId}/models/${modelId}/colors/${colorId}/stock`;
          
          const response = await api.get(endpoint);
          const availableStock = response.data.stock || 0;
          
          // Stok yoksa veya sepetteki miktar stoktan fazlaysa
          if (availableStock === 0 || item.quantity > availableStock) {
            outOfStock.push({
              id: item.id,
              product: item.product,
              model: item.selectedModel || item.phone_model,
              color: item.selectedColor || item.color,
              availableStock,
              requestedQuantity: item.quantity
            });
          }
        } catch (error) {
          console.error('Stok sorgulama hatası:', error);
          // Hata durumunda da ürünü stokta yok olarak işaretle
          outOfStock.push({
            id: item.id,
            product: item.product,
            model: item.selectedModel || item.phone_model,
            color: item.selectedColor || item.color,
            availableStock: 0,
            requestedQuantity: item.quantity,
            error: true
          });
        }
      }
      
      setOutOfStockItems(outOfStock);
      
      // Stokta olmayan ürünler bulunduysa sepet sayfasına yönlendir
      if (outOfStock.length > 0) {
        toast.error('Sepetinizdeki bazı ürünlerin stok durumu değişti. Lütfen sepetinizi güncelleyin.', {
          duration: 5000,
        });
        
        // 2 saniye bekle, sonra sepet sayfasına geri dön
        setTimeout(() => {
          navigate('/cart');
        }, 2000);
      }
    } catch (error) {
      console.error('Stok doğrulama hatası:', error);
    } finally {
      setIsValidatingStock(false);
    }
  };

  const handleCityChange = (e) => {
    const cityValue = e.target.value;
    const selectedCityData = cities.find(city => city.name === cityValue);
    
    setSelectedCity(cityValue);
    setFormData({...formData, city: cityValue, district: ''});
    
    if (selectedCityData) {
      // Seçilen şehrin ID'sini kullanarak ilçeleri getir
      console.log('🏙️ Şehir değişti:', selectedCityData.name, 'ID:', selectedCityData.id);
      loadDistricts(selectedCityData.id);
    } else {
      setDistricts([]);
    }
  };

  const handleDistrictChange = (e) => {
    const districtName = e.target.value;
    setFormData({...formData, district: districtName});
  };

  const handleExpireMonthChange = (e) => {
    let value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setFormData({...formData, expireMonth: value});
    
    // 2 hane girildiğinde yıl alanına geç
    if (value.length === 2) {
      const yearInput = document.querySelector('input[name="expireYear"]');
      if (yearInput) yearInput.focus();
    }
  };

  const handleExpireYearChange = (e) => {
    let value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setFormData({...formData, expireYear: value});
    
    // 2 hane girildiğinde CVC alanına geç
    if (value.length === 2) {
      const inputs = document.querySelectorAll('input');
      const currentInput = document.querySelector('input[name="expireYear"]');
      const currentIndex = Array.from(inputs).indexOf(currentInput);
      if (currentIndex !== -1 && inputs[currentIndex + 1]) {
        inputs[currentIndex + 1].focus();
      }
    }
  };

  const handleCardNumberPaste = async (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Remove any non-digits and limit to 16 digits
    const cleanNumber = pastedText.replace(/\D/g, '').slice(0, 16);
    
    // Add spaces every 4 digits
    const formattedValue = cleanNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
    
    setFormData(prev => ({ ...prev, cardNumber: formattedValue }));

    // Sadece kredi kartı seçildiğinde taksit seçeneklerini kontrol et
    if (paymentMethod === 'creditCard' && cleanNumber.length >= 6) {
      await checkInstallmentOptions(cleanNumber.substring(0, 6));
    }
  };

  const handleCardNumberChange = async (e) => {
    // Remove any non-digits and limit to 16 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 16);
    
    // Add spaces every 4 digits
    const formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    
    setFormData(prev => ({ ...prev, cardNumber: formattedValue }));

      // Sadece kredi kartı seçildiğinde taksit seçeneklerini kontrol et
  if (paymentMethod === 'creditCard' && value.length >= 6) {
    await checkInstallmentOptions(value.substring(0, 6));
  } else if (value.length < 6) {
    setInstallmentOptions([]);
    setSelectedInstallment('1');
  }
  };

  // Taksit seçeneklerini kontrol eden yardımcı fonksiyon
  const checkInstallmentOptions = async (binNumber) => {
    if (!binNumber || binNumber.length !== 6) {
      console.log('Geçersiz BIN numarası:', binNumber);
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/orders/payment/installments', {
        binNumber,
        price: calculateTotal().toString()
      });
      
      if (response.data.status === 'success') {
        setInstallmentOptions(response.data.installmentDetails);
        setSelectedInstallment('1');
        console.log('Taksit seçenekleri güncellendi:', response.data.installmentDetails);
      } else {
        console.log('Taksit seçenekleri alınamadı:', response.data);
        toast.error('Taksit seçenekleri alınamadı');
      }
    } catch (error) {
      console.error('Taksit seçenekleri hatası:', error);
      toast.error('Taksit bilgileri alınamadı');
      setInstallmentOptions([]);
      setSelectedInstallment('1');
    } finally {
      setLoading(false);
    }
  };

  // SMS Doğrulama başarılı callback
  const handleSmsVerificationSuccess = (verificationData) => {
    console.log('✅ SMS doğrulama başarılı:', verificationData);
    setSmsVerified(true);
    setVerificationCode(verificationData.verificationCode);
    toast.success('Telefon numarası doğrulandı');
  };

  // SMS Doğrulama hatalı callback
  const handleSmsVerificationError = (error) => {
    console.log('❌ SMS doğrulama hatası:', error);
    setSmsVerified(false);
    setVerificationCode('');
    toast.error(error || 'SMS doğrulama başarısız');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 handleSubmit başlatıldı');
    console.log('📊 Başlangıç loading state:', loading);

    if (!kvkkAccepted || !distanceSalesAccepted) {
      toast.error('Devam etmek için lütfen KVKK Aydınlatma Metni ve Mesafeli Satış Sözleşmesi\'ni onaylayın', {
        duration: 4000,
        style: {
          background: '#ff4b4b',
          color: '#fff',
          fontWeight: 'bold',
          padding: '16px',
          borderRadius: '8px',
        },
      });
      
      // Onay kutusuna scroll yap
      document.querySelector('.kvkk-consent').scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
      
      return;
    }

    console.log('📊 setLoading(true) çağrılıyor...');
    setLoading(true);
    console.log('📊 Loading state true yapıldı');
    setButtonText(''); // Button text'i sıfırla
    setError(null);

    // Kapıda nakit ödeme için kart bilgileri kontrolü yapma
    if (paymentMethod === 'creditCard') {
      // Kart üzerindeki isim kontrolü
      const cardHolderNameWords = formData.cardHolderName.trim().split(/\s+/);
      if (cardHolderNameWords.length < 2) {
        setError('Kart üzerindeki isim en az iki kelime olmalıdır (Ad ve Soyad)');
        setLoading(false);
        return;
      }
    }

    // Kapıda nakit ödeme için gerekli alanların kontrolü
    if (paymentMethod === 'cashOnDelivery') {
      // Temel bilgilerin kontrolü
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('Ad ve soyad alanları zorunludur');
        setLoading(false);
        return;
      }
      
      if (!formData.phone.trim()) {
        setError('Telefon numarası zorunludur');
        setLoading(false);
        return;
      }
      
      if (!formData.address.trim()) {
        setError('Adres alanı zorunludur');
        setLoading(false);
        return;
      }
      
      if (!formData.city.trim()) {
        setError('Şehir seçimi zorunludur');
        setLoading(false);
        return;
      }

      // SMS doğrulama kontrolü
      if (!smsVerified || !verificationCode) {
        setError('Telefon numaranızı SMS ile doğrulamanız gerekmektedir');
        setLoading(false);
        // SMS doğrulama alanına scroll yap
        const smsElement = document.querySelector('.sms-verification');
        if (smsElement) {
          smsElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }
        return;
      }
    }

    try {
      let selectedInstallmentDetails = null;
      let totalPrice = calculateTotal();

      if (paymentMethod === 'creditCard') {
        // Seçilen taksit bilgilerini bul
        selectedInstallmentDetails = installmentOptions.find(
          option => option.installment.toString() === selectedInstallment
        );

        if (!selectedInstallmentDetails || !selectedInstallmentDetails.totalPrice) {
          setError('Taksit bilgisi bulunamadı veya geçersiz');
          setLoading(false);
          return;
        }

        // Taksit tutarının sayısal değer olduğundan emin ol
        if (isNaN(totalPrice)) {
          setError('Geçersiz taksit tutarı');
          setLoading(false);
          return;
        }
      }

      // İyzico için sepet öğeleri
      const basketItems = cart.map(item => {
        const itemPrice = parseFloat(item.product.price);
        const quantity = parseInt(item.quantity);
        const productName = item.product.name;
        const modelName = getModelInfo(item);
        const colorName = getColorInfo(item);
        
        // Ürün adını model ve renk bilgisiyle zenginleştir
        const fullProductName = `${productName} - ${modelName} - ${colorName}`;

        return {
          id: item.product_id.toString(),
          name: fullProductName,
          category1: "Phone Cases",
          itemType: "PHYSICAL",
          price: itemPrice.toFixed(2), // Birim fiyat
          quantity: quantity // Miktar bilgisi
        };
      });

      // Backend için sepet öğeleri - Yeni yapıya göre düzenlendi
      const cart_items = cart.map(item => {
        // Telefon modeli ID'sini al
        let phoneModelId = null;
        if (item.selectedModel && item.selectedModel.id) {
          phoneModelId = item.selectedModel.id;
        } else if (item.phone_model && item.phone_model.id) {
          phoneModelId = item.phone_model.id;
        } else if (item.phone_model_id) {
          phoneModelId = item.phone_model_id;
        }

        // Renk ID'sini al
        let colorId = null;
        if (item.selectedColor && item.selectedColor.id) {
          colorId = item.selectedColor.id;
        } else if (item.color && item.color.id) {
          colorId = item.color.id;
        } else if (item.color_id) {
          colorId = item.color_id;
        }

        return {
          product_id: item.product_id || item.product.id,
          phone_model_id: phoneModelId,
          color_id: colorId,
          quantity: parseInt(item.quantity),
          price: parseFloat(item.product.price),
          name: item.product.name,
          model_name: getModelInfo(item),
          color_name: getColorInfo(item)
        };
      });

      // İndirim bilgisini ekle
      const orderItems = {
        items: cart_items
      };
      
      // İndirim varsa ekle
      if (discount) {
        orderItems.discount = {
          code: discount.code,
          percentage: discount.percentage,
          name: discount.name,
          amount: calculateDiscount()
        };
      }

      const orderData = {
        locale: "tr",
        conversationId: `ORD-${new Date().toISOString().split('T')[0].replace(/-/g, '-')}-${Math.floor(Math.random() * 1000)}`,
        price: totalPrice.toFixed(2), // Toplam sepet tutarı
        paidPrice: selectedInstallment === '1' ? 
          totalPrice.toFixed(2) : // Tek çekim
          parseFloat(selectedInstallmentDetails.totalPrice).toFixed(2), // Taksitli ödeme
        installment: parseInt(selectedInstallment),
        currency: "TRY",
        basketId: `ORD-${new Date().toISOString().split('T')[0].replace(/-/g, '-')}-${Math.floor(Math.random() * 1000)}`,
        paymentGroup: "PRODUCT",
        paymentCard: {
          cardHolderName: formData.cardHolderName,
          cardNumber: formData.cardNumber.replace(/\s/g, ''),
          expireMonth: formData.expireMonth,
          expireYear: formData.expireYear,
          cvc: formData.cvc,
          registerCard: 0
        },
        cardData: {
          cardHolderName: formData.cardHolderName,
          cardNumber: formData.cardNumber.replace(/\s/g, ''),
          expireMonth: formData.expireMonth,
          expireYear: formData.expireYear,
          cvc: formData.cvc
        },
        cust_id: user?.id || null, // Misafir kullanıcılar için null
        buyer: {
          id: user?.id || `BY${new Date().getTime()}`,
          name: formData.firstName,
          surname: formData.lastName,
          email: formData.email,
          gsmNumber: formData.phone,
          identityNumber: "74300864791",
          registrationAddress: formData.address,
          city: formData.city,
          country: "Turkey",
          ip: "85.34.78.112"
        },
        shippingAddress: {
          contactName: `${formData.firstName} ${formData.lastName}`,
          city: formData.city,
          country: "Turkey",
          address: formData.address
        },
        billingAddress: {
          contactName: `${formData.firstName} ${formData.lastName}`,
          city: formData.city,
          country: "Turkey",
          address: formData.address
        },
        basketItems,
        cart_items: orderItems,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        district: formData.district,
        address: formData.address,
        total_amount: selectedInstallment === '1' ? 
          totalPrice.toFixed(2) : // Tek çekim
          parseFloat(selectedInstallmentDetails.totalPrice).toFixed(2), // Taksitli ödeme
        discount: discount ? {
          code: discount.code,
          percentage: discount.percentage,
          name: discount.name,
          amount: calculateDiscount()
        } : null, // İndirim bilgisini detaylı olarak ekle
                campaign: campaignData && campaignData.campaignActive ? {
          type: 'buy_x_get_x',
          requiredItems: campaignData.requiredItems,
          freeItems: campaignData.freeItems,
          savings: campaignData.savings,
          message: campaignData.message,
          campaignDetails: campaignData.campaignDetails
        } : null, // Kampanya bilgisini ekle
      };

      console.log('🔍 Order data campaign field:', orderData.campaign);



       if (paymentMethod === 'cashOnDelivery') {
        console.log('🚀 Kapıda nakit ödeme başlatılıyor...');
        console.log('📊 Loading state:', loading);
        console.log('💳 Payment method:', paymentMethod);
        
        // Kapıda nakit ödeme için Hepsijet entegrasyonu
        const cashOnDeliveryData = {
          orderId: orderData.conversationId,
          totalAmount: totalPrice,
          cust_id: user?.id || null, // Misafir kullanıcılar için null
          email: user?.email || formData.email || `guest_${new Date().getTime()}@mrbrown.com`,
          recipientAddress: {
            name: `${formData.firstName} ${formData.lastName}`,
            phone: formData.phone,
            address: formData.address,
            city: formData.city,
            district: formData.district
          },
                  items: cart_items,
        discount: orderData.discount || null,
        campaign: campaignData && campaignData.campaignActive ? {
          type: 'buy_x_get_x',
          requiredItems: campaignData.requiredItems,
          freeItems: campaignData.freeItems,
          savings: campaignData.savings,
          message: campaignData.message,
          campaignDetails: campaignData.campaignDetails
        } : null, // Kampanya bilgisini ekle
        verificationCode: verificationCode // SMS doğrulama kodu
        };

        console.log('📦 Cash on delivery data:', cashOnDeliveryData);
        console.log('🔍 Campaign data being sent:', cashOnDeliveryData.campaign);

        // Kapıda nakit ödeme siparişi oluştur
        console.log('📡 API çağrısı yapılıyor...');
        const codResponse = await api.post('/payment/cash-on-delivery', cashOnDeliveryData);
        console.log('✅ API yanıtı alındı:', codResponse.data);
        
        if (codResponse.data.success) {
          console.log('🎉 Kapıda nakit ödeme başarılı!');
          console.log('📊 Loading state güncelleniyor...');
          
          // Sipariş başarılı, başarı sayfasına yönlendir
          toast.success('Kapıda nakit ödeme siparişiniz başarıyla oluşturuldu!');
          clearCart();
          
          // Misafir kullanıcılar için özel mesaj
          const successMessage = user ? 
            'Kapıda nakit ödeme siparişiniz başarıyla oluşturuldu!' :
            'Kapıda nakit ödeme siparişiniz başarıyla oluşturuldu! Sipariş bilgileriniz e-posta adresinize gönderilecektir.';
          
          toast.success(successMessage);
          
          // Button text'i güncelle ve loading state'i sıfırla
          console.log('🔘 Button text güncelleniyor...');
          setButtonText('Başarılı!');
          setLoading(false);
          
          console.log('📊 Button text ve loading state güncellendi, şimdi navigate çağrılıyor...');
          
          console.log('🧭 Navigate çağrılıyor...');
          navigate('/payment/cod-success', { 
            state: { 
              orderId: orderData.conversationId,
              deliveryId: codResponse.data.deliveryId,
              trackingNumber: codResponse.data.trackingNumber,
              paymentMethod: 'cashOnDelivery',
              isGuest: !user
            } 
          });
          
          console.log('🧭 Navigate tamamlandı, setTimeout ile gecikme...');
          // Başarılı kapıda nakit ödeme için flag set et
          window.codSuccess = true;
          
          console.log('🏁 COD success flag set edildi, return ile çıkılıyor...');
          return;
        } else {
          console.log('❌ Kapıda nakit ödeme başarısız:', codResponse.data);
          throw new Error('Kapıda nakit ödeme siparişi oluşturulamadı');
        }
      } else {
        // Kredi kartı ödeme
        const response = await api.post('/orders/create-embedded', orderData);
        const data = response.data;

        if (data.status === 'pending_3d' && data.threeDSHtmlContent) {
          setShowThreeDSecure(true);
          setThreeDSecureContent(data.threeDSHtmlContent);
          // 3D Secure modal açıldığında loading state'i temizle
          setLoading(false);
        } else {
          setError('3D Secure doğrulama gerekli');
          navigate('/payment/failed');
        }
      }
    } catch (error) {
      console.log('❌ Hata yakalandı:', error);
      setError(error.response?.data?.error || error.message || 'Ödeme işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      navigate('/payment/failed');
    } finally {
      console.log('🔧 Finally bloğu çalışıyor...');
      
      // Kapıda nakit ödeme başarılı olduysa loading state'i güncelleme
      if (window.codSuccess) {
        console.log('🏁 COD success flag bulundu, loading state güncellenmiyor...');
        // Flag'i temizle
        delete window.codSuccess;
        return;
      }
      
      console.log('📊 Loading state güncelleniyor (finally)...');
      setLoading(false);
      console.log('📊 Loading state güncellendi (finally)');
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.product?.price || 0) * item.quantity;
    }, 0);
  };

  const [shippingFee, setShippingFee] = useState(0);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(300);
  const [codShippingFee, setCodShippingFee] = useState(0);
  const [shippingEnabled, setShippingEnabled] = useState(true);

  // Kargo ücreti hesaplama - parametrik olarak
  const calculateShipping = async () => {
    try {
      const subtotal = calculateSubtotal();
      
      // Payment method'u API formatına çevir
      const apiPaymentMethod = paymentMethod === 'cashOnDelivery' ? 'cod' : null;
      
      // API'den kargo hesaplama
      const response = await api.post('/parameters/calculate-shipping', {
        cartTotal: subtotal,
        paymentMethod: apiPaymentMethod
      });
      
      if (response.data.success) {
        const { shippingFee, freeShippingThreshold, codShippingFee, shippingEnabled } = response.data.data;
        setShippingFee(shippingFee);
        setFreeShippingThreshold(freeShippingThreshold);
        setCodShippingFee(codShippingFee || 0);
        // shippingEnabled null ise (kargo pasif) false olarak ayarla
        setShippingEnabled(shippingEnabled === true);
        return shippingFee;
      } else {
        throw new Error('Kargo hesaplama başarısız');
      }
    } catch (error) {
      console.error('Kargo ücreti hesaplanırken hata:', error);
      // Hata durumunda null döndür, statik değerlere dönme
      return null;
    }
  };

  // Kargo ücreti hesaplama (senkron versiyon - state'den alır)
  const getShippingFee = () => {
    if (shippingEnabled === false) {
      return 0; // Kargo pasifse ücretsiz
    }
    return shippingFee !== null ? shippingFee : 0; // null ise 0 döndür
  };

  const calculateDiscount = () => {
    if (!discount) return 0;
    return (calculateSubtotal() * discount.percentage) / 100;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = getShippingFee();
    const discountAmount = calculateDiscount();
    const codFee = paymentMethod === 'cashOnDelivery' && codShippingFee > 0 ? codShippingFee : 0;
    
    // Kampanya indirimi hesapla
    const campaignDiscount = campaignData && campaignData.campaignActive ? campaignData.savings : 0;
    
    return subtotal + shipping + codFee - discountAmount - campaignDiscount;
  };

  const formatPrice = (price) => {
    return price.toFixed(2);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    return `/api/images/${imageUrl}`;
  };

  const getModelInfo = (item) => {
    if (item.selectedModel) {
      return `${item.selectedModel.brand} ${item.selectedModel.model_name}`;
    }

    if (item.phone_model) {
      if (typeof item.phone_model === 'object' && item.phone_model !== null) {
        return `${item.phone_model.brand} ${item.phone_model.model_name}`;
      }
      return item.phone_model;
    }

    if (item.model_name) {
      return item.model_name;
    }

    return 'Belirtilmemiş';
  };

  const getColorInfo = (item) => {
    if (item.selectedColor) {
      return item.selectedColor.name;
    }

    if (item.color) {
      if (typeof item.color === 'object' && item.color !== null) {
        return item.color.name;
      }
      return item.color;
    }

    if (item.color_name) {
      return item.color_name;
    }

    return 'Belirtilmemiş';
  };

  const getColorCode = (item) => {
    if (item.selectedColor && item.selectedColor.code) {
      return item.selectedColor.code;
    }

    if (item.color && typeof item.color === 'object' && item.color.code) {
      return item.color.code;
    }

    return '#CCCCCC'; // Varsayılan gri renk
  };

  // 3D Secure modal component
  const ThreeDSecureModal = ({ htmlContent, onClose }) => {
    const { clearCart } = useCart();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(true);

    useEffect(() => {
        if (containerRef.current) {
            try {
                // 1. Base64 decode işlemi
                let decodedHtml;
                try {
                    decodedHtml = atob(htmlContent);
                    decodedHtml = decodeURIComponent(escape(decodedHtml));
                } catch (decodeError) {
                    console.error('Base64 decode error:', decodeError);
                    throw new Error('3D Secure içeriği decode edilemedi');
                }

                // 2. HTML içeriğini parse et
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = decodedHtml;

                // 3. Form elementlerini kontrol et
                const form = tempDiv.querySelector('form');
                if (!form) {
                    throw new Error('3D Secure formu bulunamadı');
                }

                // 4. Form özelliklerini al
                const action = form.action;
                const method = form.method || 'POST';

                // 5. Input alanlarını topla
                const inputs = Array.from(form.querySelectorAll('input'))
                    .map(input => {
                        const name = input.name;
                        const value = input.value;
                        const type = input.type;
                        const encodedValue = value.replace(/[<>'"&]/g, char => ({
                            '<': '&lt;',
                            '>': '&gt;',
                            "'": '&#39;',
                            '"': '&quot;',
                            '&': '&amp;'
                        }[char]));
                        return `<input type="${type}" name="${name}" value="${encodedValue}">`;
                    }).join('');

                // 6. Yeni form ve iframe içeriğini hazırla
                const iframeContent = `
                    <form id="3ds-form" action="${action}" method="${method}" target="3ds-iframe" style="display:none;">
                        ${inputs}
                    </form>
                    <iframe 
                        name="3ds-iframe" 
                        style="width:100%;height:600px;border:none;margin:0;padding:0;" 
                        onload="handleIframeLoad(this)"
                        allow="payment"
                    ></iframe>
                `;

                // 3D Secure sonucunu işle (önceden tanımla)
                const handle3DSResult = async (mdStatus, status, paymentId, conversationId) => {
                    setIsProcessing(false);

                    if (mdStatus === '1' || status === 'success') {
                        console.log('Ödeme başarılı');
                        
                        try {
                            // Önce sepeti ve indirim bilgilerini temizle
                            await clearCart();
                            console.log('Checkout: Sepet ve indirim bilgileri temizlendi');
                            
                            // Sipariş durumunu güncelle
                            await api.post('/orders/update-status', {
                                paymentId,
                                conversationId,
                                status: 'success',
                                mdStatus
                            });

                            setShowThreeDSecure(false);
                            window.location.href = `/payment/success?orderId=${conversationId}`;
                        } catch (error) {
                            console.error('İşlem sırasında hata:', error);
                            // Hata olsa bile başarılı sayfasına yönlendir
                            window.location.href = `/payment/success?orderId=${conversationId}`;
                        }
                        
                        return;
                    }

                    if (mdStatus === '0' || 
                        mdStatus === '2' || 
                        mdStatus === '3' || 
                        mdStatus === '4' || 
                        mdStatus === '5' || 
                        mdStatus === '6' || 
                        mdStatus === '7' || 
                        mdStatus === '8' ||
                        status === 'failure') {
                        console.log('Ödeme başarısız');
                        
                        api.post('/orders/update-status', {
                            paymentId,
                            conversationId,
                            status: 'failed',
                            mdStatus
                        }).then(() => {
                            setShowThreeDSecure(false);
                            window.location.href = `/payment/failed?orderId=${conversationId}`;
                        }).catch(error => {
                            console.error('Sipariş güncelleme hatası:', error);
                            setShowThreeDSecure(false);
                            window.location.href = '/payment/failed';
                        });
                        
                        return;
                    }
                };

                // 7. iframe yüklenme fonksiyonunu global scope'a ekle
                window.handleIframeLoad = async (iframe) => {
                    try {
                        if (!iframe.contentWindow) return;
                        
                        // Cross-origin iframe'e erişmeye çalış - CORS hatası normal
                        let iframeUrl = null;
                        try {
                            iframeUrl = iframe.contentWindow.location.href;
                            console.log('3DS iframe URL:', iframeUrl);
                        } catch (corsError) {
                            // CORS hatası normal - iframe cross-origin olduğu için
                            // Bu durumda iframe yüklendi demektir, loading state'ini güncelle
                            console.log('3DS iframe yüklendi (CORS nedeniyle URL okunamıyor - bu normal, localhost\'ta beklenen bir durum)');
                            setIsProcessing(false);
                            return;
                        }

                        if (!iframeUrl || iframeUrl === 'about:blank') return;

                        const urlParams = new URLSearchParams(iframeUrl.split('?')[1]);
                        const mdStatus = urlParams.get('mdStatus');
                        const status = urlParams.get('status');
                        const paymentId = urlParams.get('paymentId');
                        const conversationId = urlParams.get('conversationId');

                        if (mdStatus || status) {
                            handle3DSResult(mdStatus, status, paymentId, conversationId);
                            return;
                        }

                        if (iframeUrl.includes('/payment/success')) {
                            setShowThreeDSecure(false);
                            window.location.href = iframeUrl;
                            return;
                        } else if (iframeUrl.includes('/payment/failed')) {
                            setShowThreeDSecure(false);
                            window.location.href = iframeUrl;
                            return;
                        }
                    } catch (e) {
                        // CORS hatası normal - sadece log'la
                        console.log('3DS iframe yüklendi (CORS nedeniyle detay okunamıyor - bu normal, localhost\'ta beklenen bir durum)');
                        setIsProcessing(false);
                    }
                };

                containerRef.current.innerHTML = iframeContent;

                setTimeout(() => {
                    const form = document.getElementById('3ds-form');
                    if (form) {
                        console.log('Submitting 3DS form...');
                        form.submit();
                    }
                }, 100);

            } catch (error) {
                console.error('3D Secure content processing error:', error);
                setIsProcessing(false);
                setShowThreeDSecure(false);
                navigate('/payment/failed');
            }
        }

        return () => {
            delete window.handleIframeLoad;
        };
    }, [htmlContent, clearCart, navigate]);

    // Modal kapatma fonksiyonunu güncelleyelim
    const handleClose = () => {
        if (window.confirm('3D Secure doğrulama işlemini iptal etmek istediğinize emin misiniz? İşlemi iptal ederseniz ödemeniz gerçekleşmeyecektir.')) {
            setShowThreeDSecure(false);
        }
    };

    return (
        <div className="three-d-secure-modal">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>3D Secure Doğrulama</h2>
                    <button onClick={handleClose} className="close-button">
                        <span>&times;</span>
                    </button>
                </div>
                <div className="modal-body" ref={containerRef}>
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>3D Secure doğrulama sayfası yükleniyor...</p>
                    </div>
                </div>
            </div>
            <style>{`
                .three-d-secure-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }
                .modal-content {
                    background: white;
                    border-radius: 8px;
                    width: 90%;
                    max-width: 800px;
                    max-height: 90vh;
                    overflow: auto;
                    position: relative;
                }
                .modal-header {
                    padding: 1rem;
                    border-bottom: 1px solid #eee;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background-color: #f8f9fa;
                    border-radius: 8px 8px 0 0;
                    position: sticky;
                    top: 0;
                    z-index: 1000;
                }
                .close-button {
                    position: absolute;
                    right: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #fff;
                    border: 1px solid #ddd;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    cursor: pointer;
                    border-radius: 50%;
                    color: #333;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    padding: 0;
                }
                .close-button:hover {
                    background-color: #f1f1f1;
                    border-color: #ccc;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.15);
                }
                .close-button span {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    line-height: 1;
                }
                .loading {
                    text-align: center;
                    padding: 2rem;
                }
                .spinner {
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #3498db;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 1rem;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @media (max-width: 768px) {
                    .modal-content {
                        width: 95%;
                        margin: 10px;
                    }
                    .modal-header {
                        padding: 0.75rem;
                    }
                    .modal-header h2 {
                        font-size: 1.25rem;
                        margin-right: 40px;
                    }
                    .close-button {
                        width: 28px;
                        height: 28px;
                        font-size: 18px;
                        right: 8px;
                    }
                }
            `}</style>
        </div>
    );
  };

  if (!cart || cart.length === 0) {
    return (
      <Container>
        <Alert variant="warning" className="mt-4">
          Sepetinizde ürün bulunmamaktadır. Lütfen önce sepetinize ürün ekleyin.
        </Alert>
      </Container>
    );
  }

  return (
    <div className="checkout-page">
      <Container>
        <h2 className="text-center mb-5">Ödeme İşlemi</h2>
        
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <Row className="g-4">
          {/* Sol Taraf - Form */}
          <Col lg={8}>
            <Form onSubmit={handleSubmit}>
              {/* Kişisel Bilgiler */}
              <Card className="checkout-card mb-4">
                <Card.Body className="p-4">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faUser} className="icon" />
                    Kişisel Bilgiler
                  </h4>
                  
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faUser} className="me-2" />
                          Ad
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Adınız"
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faUser} className="me-2" />
                          Soyad
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Soyadınız"
                          value={formData.lastName}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                          E-posta
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="E-posta adresiniz"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faPhone} className="me-2" />
                          Telefon
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="5xx xxx xx xx"
                          value={formData.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 10 && (!value || value.startsWith('5'))) {
                              setFormData({...formData, phone: value});
                            }
                          }}
                          pattern="^5[0-9]{9}$"
                          maxLength="10"
                          required
                        />
                        <Form.Text className="text-muted">
                          Telefon numaranız 5 ile başlamalı ve 10 haneli olmalıdır
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Teslimat Bilgileri */}
              <Card className="checkout-card mb-4">
                <Card.Body className="p-4">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faShippingFast} className="icon" />
                    Teslimat Bilgileri
                  </h4>
                  
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faCity} className="me-2" />
                          İl
                        </Form.Label>
                        <Form.Select
                          value={formData.city}
                          onChange={handleCityChange}
                          required
                          disabled={loadingCities}
                        >
                          <option value="">
                            {loadingCities ? 'İller yükleniyor...' : 'İl Seçiniz'}
                          </option>
                          {cities.map((city) => (
                            <option key={city.id} value={city.name}>
                              {city.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faBuilding} className="me-2" />
                          İlçe
                        </Form.Label>
                        <Form.Select
                          value={formData.district}
                          onChange={handleDistrictChange}
                          required
                          disabled={!formData.city || loadingDistricts}
                        >
                          <option value="">
                            {loadingDistricts ? 'İlçeler yükleniyor...' : 'İlçe Seçiniz'}
                          </option>
                          {districts.map((district) => (
                            <option key={district.id} value={district.name}>
                              {district.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>
                          <FontAwesomeIcon icon={faHome} className="me-2" />
                          Açık Adres
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Mahalle, sokak, apartman no, daire no vb."
                          value={formData.address}
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Ödeme Yöntemi Seçimi */}
              <Card className="checkout-card mb-4">
                <Card.Body className="p-4">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faCreditCard} className="icon" />
                    Ödeme Yöntemi
                  </h4>
                  
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group className="payment-method-radio">
                        <div className="d-flex flex-column gap-3">
                          <Form.Check
                            type="radio"
                            id="creditCard"
                            name="paymentMethod"
                            value="creditCard"
                            checked={paymentMethod === 'creditCard'}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            label={
                              <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faCreditCard} className="me-2 text-primary" />
                                <span className="fw-bold">Kredi Kartı ile Ödeme</span>
                                <small className="text-muted ms-2">(Güvenli ödeme)</small>
                              </div>
                            }
                          />
                          
                          <Form.Check
                            type="radio"
                            id="cashOnDelivery"
                            name="paymentMethod"
                            value="cashOnDelivery"
                            checked={paymentMethod === 'cashOnDelivery'}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            label={
                              <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faTruck} className="me-2 text-success" />
                                <span className="fw-bold">Kapıda Nakit Ödeme</span>
                                <small className="text-muted ms-2">(Teslimat sırasında ödeme{codShippingFee > 0 ? ` +${codShippingFee.toFixed(2)} TL ekstra ücret` : ''})</small>
                              </div>
                            }
                          />
                        </div>
                      </Form.Group>
                      
                      {/* Kapıda nakit ödeme bilgi mesajı */}
                      {paymentMethod === 'cashOnDelivery' && (
                        <>
                          <div className="alert alert-info mt-3 cash-on-delivery-info">
                            <FontAwesomeIcon icon={faTruck} className="me-2" />
                            <strong>Kapıda Nakit Ödeme:</strong> Siparişiniz teslimat sırasında nakit olarak ödenecektir. 
                            Kargo ücreti dahil toplam tutar teslimat sırasında tahsil edilecektir.
                            {codShippingFee > 0 && (
                              <div className="mt-2">
                                <strong>⚠️ Ekstra Ücret:</strong> Kapıda nakit ödeme için {codShippingFee.toFixed(2)} TL ekstra ücret alınmaktadır.
                              </div>
                            )}
                          </div>

                          {/* SMS Doğrulama - Sadece kapıda nakit ödeme seçildiğinde göster */}
                          {paymentMethod === 'cashOnDelivery' && (
                            <SMSVerification
                              phoneNumber={formData.phone}
                              onVerificationSuccess={handleSmsVerificationSuccess}
                              onVerificationError={handleSmsVerificationError}
                              className="sms-verification"
                            />
                          )}
                        </>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Kart Bilgileri - Sadece kredi kartı seçildiğinde göster */}
              {paymentMethod === 'creditCard' && (
              <Card className="checkout-card mb-4">
                <Card.Body className="p-4">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faCreditCard} className="icon" />
                    Kart Bilgileri
                  </h4>
                  
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Kart Üzerindeki İsim</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Kart üzerindeki ad soyad"
                          value={formData.cardHolderName}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setFormData({...formData, cardHolderName: value});
                          }}
                          required={paymentMethod === 'creditCard'}
                          pattern="^[A-ZÇĞİÖŞÜ\s]{2,}(\s+[A-ZÇĞİÖŞÜ\s]{2,})+$"
                          title="Lütfen en az iki kelimeden oluşan ad ve soyadınızı giriniz"
                        />
                        <Form.Text className="text-muted">
                          Kart üzerindeki ad ve soyadınızı giriniz (örn: Emre Karabulut)
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Kart Numarası</Form.Label>
                        <Form.Control
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleCardNumberChange}
                          onPaste={handleCardNumberPaste}
                          placeholder="Kart Numarası"
                          required={paymentMethod === 'creditCard'}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={6} sm={4}>
                      <Form.Group>
                        <Form.Label>Son Kullanma Ay</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="AA"
                          name="expireMonth"
                          value={formData.expireMonth}
                          onChange={handleExpireMonthChange}
                          maxLength="2"
                          required={paymentMethod === 'creditCard'}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={6} sm={4}>
                      <Form.Group>
                        <Form.Label>Son Kullanma Yıl</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="YY"
                          name="expireYear"
                          value={formData.expireYear}
                          onChange={handleExpireYearChange}
                          maxLength="2"
                          required={paymentMethod === 'creditCard'}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} sm={4}>
                      <Form.Group>
                        <Form.Label>CVC</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="123"
                          value={formData.cvc}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({...formData, cvc: value});
                          }}
                          maxLength="3"
                          required={paymentMethod === 'creditCard'}
                        />
                      </Form.Group>
                    </Col>

                    {paymentMethod === 'creditCard' && installmentOptions.length > 0 && (
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Taksit Seçenekleri</Form.Label>
                          <Form.Select
                            value={selectedInstallment}
                            onChange={(e) => setSelectedInstallment(e.target.value)}
                          >
                            {installmentOptions.map((option, index) => (
                              <option key={`${option.installment}-${option.totalPrice}-${index}`} value={option.installment}>
                                {option.installment} Taksit - 
                                {option.installment === 1 
                                  ? `Tek Çekim ${option.totalPrice} TL`
                                  : `${option.installment} x ${option.monthlyPayment} TL = ${option.totalPrice} TL ${option.installmentFee > 0 ? '(Vade Farkı Dahil)' : ''}`
                                }
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              )}
            </Form>
          </Col>

          {/* Sağ Taraf - Sipariş Özeti */}
          <Col lg={4}>
            <Card className="checkout-card order-summary">
              <Card.Body className="p-4">
                <h4 className="section-title mb-4">
                  <FontAwesomeIcon icon={faShoppingCart} className="icon" />
                  Sipariş Özeti
                </h4>

                {/* Sepetteki Ürünler */}
                <div className="cart-items mb-4">
                  {cart.map((item) => (
                    <div key={item.id} className="checkout-item">
                      <div className="checkout-item-image">
                        <img src={getImageUrl(item.product.images)} alt={item.product.name} />
                      </div>
                      <div className="checkout-item-details">
                        <h5 className="checkout-item-name">{item.product.name}</h5>
                        <div className="checkout-item-info">
                          {(item.selectedModel || item.phone_model) && (
                            <div className="model-info">
                              <span className="info-label">Model:</span>
                              <span className="info-value">
                                {getModelInfo(item)}
                              </span>
                            </div>
                          )}
                          {(item.selectedColor || item.color) && (
                            <div className="color-info">
                              <span className="info-label">Renk:</span>
                              <span className="info-value">
                                <span 
                                  className="color-circle" 
                                  style={{ 
                                    backgroundColor: getColorCode(item),
                                    border: (getColorCode(item) === '#FFFFFF' || getColorCode(item) === '#FFFFFF' || 
                                            getColorCode(item) === 'white' || getColorCode(item) === 'white') ? '1px solid #ddd' : 'none'
                                  }}
                                ></span>
                                {getColorInfo(item)}
                              </span>
                            </div>
                          )}
                          <div className="quantity-info">
                            <span className="info-label">Adet:</span>
                            <span className="info-value">{item.quantity}</span>
                          </div>
                          <div className="price-info">
                            <span className="info-label">Fiyat:</span>
                            <span className="info-value">{formatPrice(item.product.price * item.quantity)} TL</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Kampanya Bilgisi */}
                <CampaignCalculator 
                  cartItems={cart}
                  onCampaignCalculated={setCampaignData}
                  showInSummary={true}
                />

                {/* Fiyat Detayları */}
                <div className="price-details">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Ara Toplam</span>
                    <span>{formatPrice(calculateSubtotal())} TL</span>
                  </div>
                  
                  {discount && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>İndirim ({discount.percentage}%)</span>
                      <span>-{formatPrice(calculateDiscount())} TL</span>
                    </div>
                  )}
                  
                  {campaignData && campaignData.campaignActive && campaignData.savings > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Kampanya İndirimi</span>
                      <span>-{formatPrice(campaignData.savings)} TL</span>
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      <FontAwesomeIcon icon={faTruck} className="me-2" />
                      Kargo
                    </span>
                    <span className={getShippingFee() === 0 ? 'free-shipping text-success' : ''}>
                      {shippingEnabled === false ? (
                        <span className="text-success">Ücretsiz</span>
                      ) : getShippingFee() === 0 ? (
                        'Ücretsiz'
                      ) : (
                        `${formatPrice(getShippingFee())} TL`
                      )}
                    </span>
                  </div>
                  
                  {paymentMethod === 'cashOnDelivery' && codShippingFee > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-info">
                      <small>
                        <FontAwesomeIcon icon={faTruck} className="me-1" />
                        Kapıda nakit ödeme ücreti
                      </small>
                      <small>+{codShippingFee.toFixed(2)} TL</small>
                    </div>
                  )}
                  
                  {getShippingFee() !== null && getShippingFee() > 0 && (
                    <div className="shipping-info text-center">
                      <FontAwesomeIcon icon={faTruck} className="me-2" />
                      {freeShippingThreshold} TL üzeri alışverişlerinizde kargo ücretsiz!
                    </div>
                  )}
                  
                  {getShippingFee() !== null && getShippingFee() === 0 && (
                    <div className="shipping-info text-center text-success">
                      <FontAwesomeIcon icon={faCheck} className="me-2" />
                      Ücretsiz kargo kazandınız!
                    </div>
                  )}
                  
                  {selectedInstallment !== '1' && installmentOptions.length > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-danger">
                      <span>Vade Farkı</span>
                      <span>
                        {formatPrice(
                          parseFloat(installmentOptions.find(opt => opt.installment.toString() === selectedInstallment)?.totalPrice || 0) - calculateTotal()
                        )} TL
                      </span>
                    </div>
                  )}
                  <hr />
                  <div className="d-flex justify-content-between">
                    <strong>Toplam</strong>
                    <strong>
                      {selectedInstallment !== '1' && installmentOptions.length > 0 
                        ? formatPrice(installmentOptions.find(opt => opt.installment.toString() === selectedInstallment)?.totalPrice || calculateTotal())
                        : formatPrice(calculateTotal())
                      } TL
                    </strong>
                  </div>
                  {selectedInstallment !== '1' && installmentOptions.length > 0 && (
                    <div className="installment-info text-center mt-2">
                      <small className="text-muted">
                        {selectedInstallment} x {formatPrice(installmentOptions.find(opt => opt.installment.toString() === selectedInstallment)?.monthlyPayment || 0)} TL
                      </small>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* KVKK ve Ödeme Butonu - Sayfanın En Altında */}
        <Row className="mt-4">
          <Col xs={12}>
            <div className="kvkk-consent">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="policyConsent"
                  checked={kvkkAccepted}
                  onChange={(e) => {
                    setKvkkAccepted(e.target.checked);
                    setDistanceSalesAccepted(e.target.checked);
                  }}
                  required
                />
                <label className="form-check-label" htmlFor="policyConsent">
                  <Link to="/kvkk" target="_blank" rel="noopener noreferrer">KVKK Aydınlatma Metni</Link> ve{' '}
                  <Link to="/distance-sales-agreement" target="_blank" rel="noopener noreferrer">Mesafeli Satış Sözleşmesi</Link>
                  'ni okudum ve onaylıyorum.
                </label>
              </div>
            </div>

            <div className="d-grid mt-3">
              <Button 
                type="submit" 
                variant="primary" 
                size="lg"
                className="payment-button"
                disabled={loading || (paymentMethod === 'cashOnDelivery' && (!smsVerified || !verificationCode))}
                onClick={handleSubmit}
              >
                {loading === true ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    İşleminiz Yapılıyor...
                  </>
                ) : buttonText ? (
                  <>
                    <FontAwesomeIcon icon={faCheck} className="me-2 text-success" />
                    {buttonText}
                  </>
                ) : (
                  paymentMethod === 'cashOnDelivery' 
                    ? (smsVerified ? 'Siparişi Tamamla' : 'Önce Telefon Doğrulaması Yapın')
                    : 'Ödemeyi Tamamla'
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
      
      {showThreeDSecure && threeDSecureContent && (
        <ThreeDSecureModal 
          htmlContent={threeDSecureContent}
          onClose={() => setShowThreeDSecure(false)}
        />
      )}
    </div>
  );
}

export default Checkout; 